import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import config from "../../../../config";
import ApiStatus from "component/ApiStatus";
import { ClientWalletViewApi } from "store/slices/clientwalletSlice";

const sourceLabel = (source, t) => {
  const map = {
    deposit_refund: t("Deposit refund credit"),
    sale_payment: t("Sale payment"),
    portal_deposit: t("Online booking deposit"),
    credit: t("Credit"),
    debit: t("Debit"),
  };
  return map[source] || source || "—";
};

const money = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const Wallet = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const detail = useSelector((state) => state.client.isDetailData);
  const walletData = useSelector((state) => state.clientwallet?.isWalletData);
  const apiStatus = useSelector((state) => state.clientwallet?.isApiStatus?.ClientWalletViewApi);
  const clientId = detail?.id;

  useEffect(() => {
    if (clientId) {
      dispatch(ClientWalletViewApi({ client_id: clientId }));
    }
  }, [clientId, dispatch]);

  const balance = Number(walletData?.balance) || 0;
  const transactions = Array.isArray(walletData?.transactions) ? walletData.transactions : [];

  const summary = useMemo(() => {
    let credited = 0;
    let spent = 0;
    for (const tx of transactions) {
      const amount = Number(tx.amount) || 0;
      if (tx.type === "credit") credited += amount;
      else spent += amount;
    }
    return {
      credited: Math.round(credited * 100) / 100,
      spent: Math.round(spent * 100) / 100,
      count: transactions.length,
    };
  }, [transactions]);

  const refresh = () => {
    if (clientId) dispatch(ClientWalletViewApi({ client_id: clientId }));
  };

  return (
    <>
      <div className="drawer-header">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3 pe-md-5">
          <div>
            <h2 className="mb-1">{t("Wallet")}</h2>
            <p className="text-muted mb-0 client-wallet-subtitle">
              {t("Store credit from refunds — use it on future sales and bookings")}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={refresh}
            disabled={apiStatus === "loading"}
          >
            {apiStatus === "loading" ? (
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            ) : (
              <i className="fa fa-refresh me-1" aria-hidden="true" />
            )}
            {t("Refresh")}
          </button>
        </div>
      </div>

      <div className="content-wrp client-wallet-panel">
        <div className="client-wallet-balance-card event-box-pink mb-3">
          <div className="client-wallet-balance-card__top">
            <div>
              <span className="client-wallet-balance-card__eyebrow">{t("Available balance")}</span>
              <div className="client-wallet-balance-card__amount color-wine">${money(balance)}</div>
            </div>
            <div className="client-wallet-balance-card__icon" aria-hidden="true">
              <i className="fa fa-money" />
            </div>
          </div>
          <div className="client-wallet-balance-card__hint">
            {balance > 0
              ? t("This credit can be applied at sale checkout or on online booking deposits.")
              : t("Credits appear here after you choose Apply Credit on a deposit refund.")}
          </div>
        </div>

        <div className="row g-2 mb-4">
          <div className="col-4">
            <div className="client-wallet-stat">
              <div className="client-wallet-stat__label">{t("Total credited")}</div>
              <div className="client-wallet-stat__value text-success">+${money(summary.credited)}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="client-wallet-stat">
              <div className="client-wallet-stat__label">{t("Total spent")}</div>
              <div className="client-wallet-stat__value text-danger">-${money(summary.spent)}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="client-wallet-stat">
              <div className="client-wallet-stat__label">{t("Transactions")}</div>
              <div className="client-wallet-stat__value">{summary.count}</div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0 fw-semibold">{t("Transaction history")}</h5>
          {summary.count > 0 ? (
            <span className="badge client-wallet-count-badge">{summary.count}</span>
          ) : null}
        </div>

        {apiStatus === "loading" && transactions.length === 0 ? (
          <ApiStatus actionType="clientwalletview" />
        ) : null}

        {apiStatus === "failed" ? (
          <div className="alert alert-warning py-2 mb-0" role="alert">
            {t("Could not load wallet details.")}
            <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={refresh}>
              {t("Try again")}
            </button>
          </div>
        ) : null}

        {apiStatus === "succeeded" && transactions.length === 0 ? (
          <div className="complete-box text-center d-flex flex-column justify-content-center mt-2">
            <div className="complete-box-wrp text-center">
              <img src={config.imagepath + "nots.png"} alt="" className="mb-md-4 mb-3" />
              <h5 className="mb-2 fw-semibold">{t("No wallet transactions yet")}</h5>
              <p className="text-muted mb-0 px-md-4">
                {t(
                  "When you refund a deposit with Apply Credit, the amount is added here and can be used on the next appointment."
                )}
              </p>
            </div>
          </div>
        ) : null}

        {transactions.length > 0 ? (
          <div className="client-wallet-tx-list">
            {transactions.map((tx) => {
              const isCredit = tx.type === "credit";
              const amount = Number(tx.amount) || 0;
              return (
                <div className="client-wallet-tx-card" key={tx.id}>
                  <div className="client-wallet-tx-card__left">
                    <span
                      className={`client-wallet-tx-card__icon ${isCredit ? "is-credit" : "is-debit"}`}
                      aria-hidden="true"
                    >
                      <i className={`fa ${isCredit ? "fa-arrow-down" : "fa-arrow-up"}`} />
                    </span>
                    <div className="client-wallet-tx-card__body">
                      <div className="client-wallet-tx-card__title-row">
                        <h6 className="mb-0 fw-semibold">{sourceLabel(tx.source, t)}</h6>
                        <span className={`client-wallet-tx-card__type ${isCredit ? "is-credit" : "is-debit"}`}>
                          {isCredit ? t("Credit") : t("Debit")}
                        </span>
                      </div>
                      <div className="client-wallet-tx-card__meta">
                        {tx.created_at ? moment(tx.created_at).format("DD/MM/YYYY · hh:mm A") : "—"}
                        {tx.sale_id ? ` · Invoice #${tx.sale_id}` : ""}
                      </div>
                      {tx.note ? <p className="client-wallet-tx-card__note mb-0">{tx.note}</p> : null}
                    </div>
                  </div>
                  <div className="client-wallet-tx-card__right">
                    <div className={`client-wallet-tx-card__amount ${isCredit ? "is-credit" : "is-debit"}`}>
                      {isCredit ? "+" : "-"}${money(amount)}
                    </div>
                    <div className="client-wallet-tx-card__balance">
                      {t("Balance")}: ${money(tx.balance_after)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Wallet;
